import { Button, Dropdown, InlineLoading } from '@carbon/react';
import { ScanDepthDropdown } from 'pages/admin/scan-depth-dropdown';
import type { ScanDepth } from 'pages/admin/utils';
import styles from './page.module.scss';

interface TargetOption<T extends string> {
  id: T;
  label: string;
}

interface ScanControlsProps<T extends string> {
  idPrefix: string;
  targetLabel: string;
  targets: TargetOption<T>[];
  target: T | null;
  onTargetChange: (target: T) => void;
  scanDepth: ScanDepth;
  onScanDepthChange: (scanDepth: ScanDepth) => void;
  onScan: () => void;
  loading: boolean;
  buttonLabel: string;
}

/**
 * The "what to scan, how deep, go" row shared by the queue scan and the
 * retry/dead set scan.
 *
 * @param {ScanControlsProps} props - The target and depth choices and the scan trigger.
 * @return The controls row.
 */
export function ScanControls<T extends string>(props: ScanControlsProps<T>) {
  const {
    idPrefix,
    targetLabel,
    targets,
    target,
    onTargetChange,
    scanDepth,
    onScanDepthChange,
    onScan,
    loading,
    buttonLabel,
  } = props;

  return (
    <div className={styles.scanControls}>
      <Dropdown
        id={`${idPrefix}-target-dropdown`}
        titleText={targetLabel}
        hideLabel
        className={styles.targetDropdown}
        label={targetLabel}
        size="sm"
        items={targets}
        itemToString={(item) => (item ? item.label : '')}
        selectedItem={targets.find((option) => option.id === target) ?? null}
        onChange={({ selectedItem }) => {
          if (selectedItem) onTargetChange(selectedItem.id);
        }}
        disabled={loading}
      />
      <ScanDepthDropdown
        id={`${idPrefix}-depth-dropdown`}
        value={scanDepth}
        onChange={onScanDepthChange}
        disabled={loading}
      />
      <Button
        kind="secondary"
        size="sm"
        onClick={onScan}
        disabled={loading || target === null}
      >
        {buttonLabel}
      </Button>
      {loading && <InlineLoading description="Scanning..." />}
    </div>
  );
}
